import client from "./api";
import { getStoredPromo } from "./analytics";

const LOOKUP_KEY_BY_TIER = {
  solo: "solo_monthly",
  duo: "duo_monthly",
  trio: "trio_monthly",
  small_fleet: "small_fleet_monthly",
  growth: "growth_monthly",
};

/**
 * Kick off a Stripe Checkout Session for the given tier.
 * interval: "monthly" (default) or "annual" (2 months free).
 * Redirects the browser to Stripe's hosted checkout page.
 * Throws on any error (caller should handle w/ toast).
 */
export async function startCheckout(tier, interval = "monthly") {
  if (!LOOKUP_KEY_BY_TIER[tier]) throw new Error(`Unknown tier: ${tier}`);
  const lookup_key = interval === "annual" ? `${tier}_annual` : LOOKUP_KEY_BY_TIER[tier];
  const { data } = await client.post("/billing/checkout", {
    lookup_key,
    origin_url: window.location.origin,
    promo: getStoredPromo() || "",
  });
  if (!data.checkout_url) throw new Error("No checkout URL returned");
  window.location.href = data.checkout_url;
}

/**
 * Open the Stripe Customer Portal for the current user (manage/cancel subscription).
 */
export async function openBillingPortal() {
  const { data } = await client.post("/billing/portal", {
    return_url: `${window.location.origin}/app/billing`,
  });
  if (!data.url) throw new Error("No portal URL returned");
  window.location.href = data.url;
}

/**
 * Poll the checkout status until it's terminal (paid/failed/expired) or timeout.
 * Returns the final status object.
 */
export async function pollCheckoutStatus(sessionId, { intervalMs = 2000, maxAttempts = 20 } = {}) {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const { data } = await client.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") return data;
      if (["expired", "failed"].includes(data.payment_status)) return data;
    } catch (e) {
      // ignore transient errors and retry
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { session_id: sessionId, payment_status: "pending", status: "timeout" };
}

export { LOOKUP_KEY_BY_TIER };
